import { useEffect, useState } from "react";

import {
  adminApi,
  bytesToGb,
  createUser,
  gbToBytes,
  listProjects,
  setUserQuota,
  type ProjectRow,
  type UserRow,
} from "../api";

/** The quota the platform actually enforces. Older API responses may not carry
 *  `effective_quota_bytes`; falling back to `quota_bytes` keeps them rendering. */
function enforcedQuota(u: UserRow): number {
  return u.effective_quota_bytes ?? u.quota_bytes;
}

/**
 * भाग E4 Users: create + drill + freeze/revoke/restore + quota. Vault
 * plaintext is never shown here — this page only ever renders `UserRow`
 * metadata the Admin API itself returns, which has no bytes/key-material
 * field to render.
 */
export function Users(): JSX.Element {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [newProjectId, setNewProjectId] = useState("");
  const [externalUserId, setExternalUserId] = useState("");
  const [newQuotaGb, setNewQuotaGb] = useState<string>("");
  const [creating, setCreating] = useState(false);

  const [quotaEdits, setQuotaEdits] = useState<Record<string, string>>({});

  function reload(): void {
    adminApi
      .get<UserRow[]>("/api/v1/admin/users")
      .then(setUsers)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
    listProjects()
      .then((rows) => {
        setProjects(rows);
        if (!newProjectId && rows.length > 0) setNewProjectId(rows[0].id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }

  useEffect(reload, []);

  function projectName(id: string): string {
    return projects.find((p) => p.id === id)?.name ?? id.slice(0, 8) + "…";
  }

  async function act(userId: string, action: "freeze" | "restore" | "revoke-sessions"): Promise<void> {
    setBusyId(userId);
    try {
      await adminApi.post(`/api/v1/admin/users/${userId}/${action}`);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function handleCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!newProjectId || !externalUserId.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const quotaBytes = newQuotaGb.trim() === "" ? null : gbToBytes(Number(newQuotaGb));
      await createUser(newProjectId, externalUserId.trim(), quotaBytes);
      setExternalUserId("");
      setNewQuotaGb("");
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  }

  async function handleSetQuota(userId: string): Promise<void> {
    const raw = quotaEdits[userId];
    if (raw === undefined || raw === "") return;
    const gb = Number(raw);
    if (Number.isNaN(gb) || gb < 0) {
      setError("Quota must be a non-negative number of GB.");
      return;
    }
    setBusyId(userId);
    setError(null);
    try {
      const updated = await setUserQuota(userId, gbToBytes(gb));
      setQuotaEdits((prev) => ({ ...prev, [userId]: "" }));
      setNotice(updated.quota_warnings?.length ? updated.quota_warnings.join(" ") : null);
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <h2 className="page-title">👥 Users</h2>
      <p className="page-subtitle">
        Create, drill, freeze, restore, revoke sessions, set quota — never a vault plaintext
        bypass.
      </p>
      {error && <div className="error-banner">{error}</div>}
      {notice && <div className="notice-banner">{notice}</div>}

      <div className="panel">
        <h3>Create user</h3>
        <form onSubmit={(e) => void handleCreate(e)}>
          <div className="form-row">
            <label htmlFor="new-user-project">Project</label>
            <select
              id="new-user-project"
              value={newProjectId}
              onChange={(e) => setNewProjectId(e.target.value)}
              required
            >
              <option value="" disabled>
                Select a project…
              </option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="new-user-external-id">External user ID</label>
            <input
              id="new-user-external-id"
              value={externalUserId}
              onChange={(e) => setExternalUserId(e.target.value)}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="new-user-quota">Quota (GB, optional)</label>
            <input
              id="new-user-quota"
              type="number"
              min={0}
              placeholder="default"
              value={newQuotaGb}
              onChange={(e) => setNewQuotaGb(e.target.value)}
            />
          </div>
          <button type="submit" disabled={creating || !newProjectId || !externalUserId.trim()}>
            {creating ? "Creating…" : "Create user"}
          </button>
        </form>
      </div>

      <table>
        <thead>
          <tr>
            <th>External ID</th>
            <th>Project</th>
            <th>Status</th>
            <th>Used / Quota</th>
            <th>Set quota (GB)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id}>
              <td>{u.external_user_id}</td>
              <td>{projectName(u.project_id)}</td>
              <td>
                <span className="badge">{u.status}</span>
              </td>
              <td>
                {u.used_bytes.toLocaleString()} / {enforcedQuota(u).toLocaleString()} (
                {bytesToGb(enforcedQuota(u)).toFixed(1)} GB)
                {enforcedQuota(u) !== u.quota_bytes && (
                  <span
                    className="badge"
                    title={`Set to ${bytesToGb(u.quota_bytes).toFixed(1)} GB, but the per-user platform cap is what is enforced.`}
                  >
                    capped
                  </span>
                )}
              </td>
              <td>
                <input
                  type="number"
                  min={0}
                  style={{ width: 90 }}
                  placeholder={bytesToGb(u.quota_bytes).toFixed(1)}
                  value={quotaEdits[u.id] ?? ""}
                  onChange={(e) => setQuotaEdits((prev) => ({ ...prev, [u.id]: e.target.value }))}
                />{" "}
                <button
                  disabled={busyId === u.id || !quotaEdits[u.id]}
                  onClick={() => void handleSetQuota(u.id)}
                >
                  Set
                </button>
              </td>
              <td>
                {u.status !== "suspended" ? (
                  <button disabled={busyId === u.id} onClick={() => act(u.id, "freeze")}>
                    Freeze
                  </button>
                ) : (
                  <button disabled={busyId === u.id} onClick={() => act(u.id, "restore")}>
                    Restore
                  </button>
                )}{" "}
                <button disabled={busyId === u.id} onClick={() => act(u.id, "revoke-sessions")}>
                  Revoke sessions
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
